import { useCallback, useMemo } from "react";
import { useFiltersContext } from "../../../../pages/Workspace/UserFilters/FiltersContext";
import {
  InclusionFilter,
  InclusionMethod,
} from "../../../../legacy/types/companyFilter";
import { FILTER_MODE } from "../../Condition/constants";
import { FilterMode } from "../../Condition/types";

export const useVendorSections = () => {
  const { draftFilters, setDraftFilters } = useFiltersContext();

  // Get inclusion condition, vendor IDs, FAI IDs, and product count
  const {
    inclusionCondition,
    inclusionVendorIds,
    inclusionFaiIds,
    inclusionInstallAgeMonths,
    inclusionProductCount,
  } = useMemo(() => {
    const vendors = draftFilters?.vendors || [];
    const inclusionFilter = vendors.find(
      (vendor) => vendor.inclusionMethod !== InclusionMethod.NONE_PRESENT
    );

    // Get FAI IDs from departmentIds and roleIds
    const faiIds = [
      ...(inclusionFilter?.departmentIds || []),
      ...(inclusionFilter?.roleIds || []),
    ];

    return {
      inclusionCondition: inclusionFilter?.inclusionMethod || FILTER_MODE.ANY,
      inclusionVendorIds: inclusionFilter?.ids || [],
      inclusionFaiIds: faiIds,
      inclusionInstallAgeMonths: inclusionFilter?.installAgeMonths ?? undefined,
      inclusionProductCount: inclusionFilter?.productCount ?? undefined,
    };
  }, [draftFilters?.vendors]);

  // Get exclusion vendor IDs
  const exclusionVendorIds = useMemo(() => {
    const vendors = draftFilters?.vendors || [];
    const exclusionFilter = vendors.find(
      (vendor) => vendor.inclusionMethod === InclusionMethod.NONE_PRESENT
    );

    return exclusionFilter?.ids || [];
  }, [draftFilters?.vendors]);

  // Helper function to filter out empty sections
  const filterEmptyVendors = useCallback((vendors: InclusionFilter[]) => {
    return vendors.filter((vendor) => vendor.ids && vendor.ids.length > 0);
  }, []);

  // Handle inclusion vendor selection
  const handleInclusionVendorSelect = useCallback(
    (vendorIds: number[]) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Remove existing inclusion filter
        const filteredVendors = currentVendors.filter(
          (v) => v.inclusionMethod === InclusionMethod.NONE_PRESENT
        );

        // Find existing inclusion filter to preserve installAgeMonths and productCount
        const existingInclusion = currentVendors.find(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        // Add new inclusion filter if there are vendors selected
        if (vendorIds.length > 0) {
          filteredVendors.push({
            ids: vendorIds,
            inclusionMethod: inclusionCondition,
            installAgeMonths: existingInclusion?.installAgeMonths,
            productCount: existingInclusion?.productCount,
            departmentIds: existingInclusion?.departmentIds,
            roleIds: existingInclusion?.roleIds,
          });
        }

        // Filter out empty sections
        const finalVendors = filterEmptyVendors(filteredVendors);

        return {
          ...prev,
          vendors: finalVendors.length > 0 ? finalVendors : undefined,
        };
      });
    },
    [setDraftFilters, inclusionCondition, filterEmptyVendors]
  );

  // Handle exclusion vendor selection
  const handleExclusionVendorSelect = useCallback(
    (vendorIds: number[]) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Remove existing exclusion filter
        const filteredVendors = currentVendors.filter(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        // Add new exclusion filter if there are vendors selected
        if (vendorIds.length > 0) {
          filteredVendors.push({
            ids: vendorIds,
            inclusionMethod: InclusionMethod.NONE_PRESENT,
          });
        }

        // Filter out empty sections
        const finalVendors = filterEmptyVendors(filteredVendors);

        return {
          ...prev,
          vendors: finalVendors.length > 0 ? finalVendors : undefined,
        };
      });
    },
    [setDraftFilters, filterEmptyVendors]
  );

  // Handle condition change for inclusion section
  const handleConditionChange = useCallback(
    (condition: FilterMode) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Find and update inclusion filter
        const inclusionIndex = currentVendors.findIndex(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        if (inclusionIndex >= 0) {
          currentVendors[inclusionIndex] = {
            ...currentVendors[inclusionIndex],
            inclusionMethod: condition,
          };

          // Filter out empty sections
          const finalVendors = filterEmptyVendors(currentVendors);

          return {
            ...prev,
            vendors: finalVendors.length > 0 ? finalVendors : undefined,
          };
        } else {
          currentVendors.push({
            ids: [],
            inclusionMethod: condition,
          });

          return {
            ...prev,
            vendors: currentVendors,
          };
        }

        return prev;
      });
    },
    [setDraftFilters, filterEmptyVendors]
  );

  // Clear all vendor filters
  const handleClearFilter = useCallback(() => {
    setDraftFilters((prev) => ({
      ...prev,
      vendors: undefined,
    }));
  }, [setDraftFilters]);

  // Handle FAI selection
  const handleFaiSelect = useCallback(
    (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Find inclusion filter
        const inclusionIndex = currentVendors.findIndex(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        if (inclusionIndex >= 0) {
          // Update the inclusion filter with separated FAI data
          currentVendors[inclusionIndex] = {
            ...currentVendors[inclusionIndex],
            departmentIds:
              selectedDepartmentIds.length > 0
                ? selectedDepartmentIds
                : undefined,
            roleIds: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
          };

          return {
            ...prev,
            vendors: currentVendors,
          };
        }

        return prev;
      });
    },
    [setDraftFilters]
  );

  // Handle install age change
  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Find inclusion filter
        const inclusionIndex = currentVendors.findIndex(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        if (inclusionIndex >= 0) {
          currentVendors[inclusionIndex] = {
            ...currentVendors[inclusionIndex],
            installAgeMonths:
              installAge.min === undefined && installAge.max === undefined
                ? undefined
                : installAge,
          };

          return {
            ...prev,
            vendors: currentVendors,
          };
        }

        return prev;
      });
    },
    [setDraftFilters]
  );

  // Handle product count change
  const handleProductCountChange = useCallback(
    (productCount: { min?: number | null; max?: number | null }) => {
      setDraftFilters((prev) => {
        const currentVendors = [...(prev?.vendors || [])];

        // Find inclusion filter
        const inclusionIndex = currentVendors.findIndex(
          (v) => v.inclusionMethod !== InclusionMethod.NONE_PRESENT
        );

        if (inclusionIndex >= 0) {
          currentVendors[inclusionIndex] = {
            ...currentVendors[inclusionIndex],
            productCount:
              productCount.min === undefined && productCount.max === undefined
                ? undefined
                : productCount,
          };

          return {
            ...prev,
            vendors: currentVendors,
          };
        }

        return prev;
      });
    },
    [setDraftFilters]
  );

  return {
    inclusionCondition,
    inclusionVendorIds,
    inclusionFaiIds,
    inclusionInstallAgeMonths,
    inclusionProductCount,
    exclusionVendorIds,
    handleInclusionVendorSelect,
    handleExclusionVendorSelect,
    handleConditionChange,
    handleClearFilter,
    handleFaiSelect,
    handleInstallAgeChange,
    handleProductCountChange,
  };
};
